import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                                 from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridStatusColumnConfig
}                                                                 from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {nameOf}                                                   from '../../../ap-core/utils/ap-name-of';
import {IPpEntryPlanBookingEditData, IPpEntryPlanBookingGridItem} from './pp-plan-booking.types';
import {
  ApDynGridPropertyColumnConfig,
  FilterType,
  PropertyTemplate
}                                                                 from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {map}                                                      from 'rxjs/operators';
import {MapViewMode}                                              from '../../../ap-interface';
import {
  ApDynGridGroupColumnConfig
}                                                                 from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  PpNoDataValue
}                                                                 from '../../../plant-protection/types/pp-planning-booking.types';
import {
  ApDynGridEditColumnConfig
}                                                                 from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {FieldStore}                                               from '../../../stores/farm/field.store';
import {DriverStore}                                              from '../../../stores/docu/driver.store';
import {ApDateService}                                            from '../../../ap-core/services/ap-date-service';
import {MachineStore}                                             from '../../../stores/docu/machine.store';
import {InstrumentStore}                                          from '../../../stores/docu/instrument.store';
import {
  ApNutrientService
}                                                                 from '../../../ap-utils/service/ap-nutrient.service';
import {
  ApTranslationService
}                                                                 from '../../../ap-utils/service/ap-translation.service';
import {GetRoundNumericPipe}                                      from '../../../ap-utils';
import {MapViewStore}                                             from '../../../stores/layout/mapview.store';
import {
  GetRoundNumericService
}                                                                 from '../../../ap-utils/service/get-round-numeric.service';
import {
  PpCommonService
}                                                                 from '../../../plant-protection/services/pp-common.service';
import {CropTypeStore}                                            from '../../../stores/base-data/crop.types.store';
import {
  IGridStatus
}                                                                 from '../../../ap-interface/interfaces/ap-grid-status.interface';
import {Injectable}                                               from '@angular/core';
import {
  FormatType
}                                                                 from '../../../ap-utils/types/ap-format-duration.types';
import {
  ApTooltipContentService
}                                                                 from '../../../ap-utils/service/ap-tooltip-content.service';
import {
  TimeFormatPipe
}                                                                 from '../../../ap-utils/pipes/ap-format-duration.pipe';
import {TimeAreaService}                                          from '../../../services/data/time-area.service';
import ISettings = Data.BaseData.ISettings;
import ILanguage = Data.Language.ILanguage;
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;
import IDriver = Data.DocuContext.Driver.IDriver;

@Injectable()
export class PpPlanBookingGridConfig {
  constructor(private fieldStore: FieldStore,
              private driverStore: DriverStore,
              private mapViewStore: MapViewStore,
              private machineStore: MachineStore,
              private cropTypeStore: CropTypeStore,
              private instrumentStore: InstrumentStore,
              private dateService: ApDateService,
              private timeAreaService: TimeAreaService,
              private ppCommonService: PpCommonService,
              private nutrientService: ApNutrientService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private tooltipContentService: ApTooltipContentService,
              private timeFormatPipe: TimeFormatPipe,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  public getItems(planBooks: IPlantProtectionPlanBook[], isBooked: boolean): IPpEntryPlanBookingGridItem[] {
    return planBooks
      .filter(planBook => !planBook.DeletedAt)
      .filter(planBook => isBooked ? !!planBook.BookedAt : !planBook.BookedAt)
      .map(planBook => {
        const field = this.fieldStore.getFieldById(planBook.FieldId);
        const geom = this.fieldStore.getFieldGeomById(planBook.FieldGeomId);
        const driver = this.driverStore.getDriverById(planBook.ExportDriver);
        const machine = this.machineStore.getMachineById(planBook.ExportMachine);
        const instrument = this.instrumentStore.getInstrumentById(planBook.ExportInstrument);
        const isWorkTypeSpraying = this.ppCommonService.isWorkTypeSpraying(planBook);
        const waterSum = isWorkTypeSpraying ? this.ppCommonService.getWaterAmountBasedOnOperationMode(planBook) : PpNoDataValue;
        const waterUnit = isWorkTypeSpraying ? this.translationService.translate('Api_Enums__Litre') : undefined;
        const areaHa = geom?.AreaHa ?? 0;
        const status = this.getStatus(planBook, isBooked);
        const haPerHours = this.timeAreaService.getHaPerHoursFromTotalMilliseconds(geom?.AreaHa,
          planBook.Duration?.TotalMilliseconds);
        const item: IPpEntryPlanBookingGridItem = {
          Id: planBook.Id,
          Status: status,
          FieldId: field?.Id,
          FieldName: field?.FieldName,
          FieldNumber: field?.FieldNumber,
          FieldSubnumber: field?.FieldSubnumber,
          FieldNumberWithSubnumber: this.fieldStore.getFieldNumberAndSubNumber(field),
          AreaHa: areaHa,
          EcValue: planBook.EcValue,
          Crop: this.cropTypeStore.getDefaultCroptype(planBook.MainCropId)?.Description ?? '',
          ProductAverages: this.ppCommonService.getAppliedProductsExpr(planBook.ProductList),
          ProductAveragesTooltip: this.ppCommonService.getAppliedProductsExpr(planBook.ProductList, true),
          ProductSum: isWorkTypeSpraying ? this.ppCommonService.getAppliedProductSumExpr(planBook.ProductList, {
            areaHa,
            withoutProdName: true
          }) : undefined,
          ProductSumTooltip: isWorkTypeSpraying ? this.ppCommonService.getAppliedProductSumExpr(planBook.ProductList, {
            areaHa,
            isTooltip: true
          }) : undefined,
          WaterRate: isWorkTypeSpraying ? this.ppCommonService.getApplicationValue(planBook) : PpNoDataValue,
          WaterSum: isWorkTypeSpraying ? this.ppCommonService.getWaterAmountBasedOnOperationMode(planBook) : PpNoDataValue,
          WaterSumTooltip: isWorkTypeSpraying && waterSum >= 0 ? `${waterSum ?? 0} ${waterUnit}` : '',
          ApplicationDate: this.dateService.toFarmDate(planBook.ApplicationDate).toDate(),
          DurationInMilliseconds: planBook.Duration?.TotalMilliseconds,
          HaPerHour: haPerHours,
          Driver: driver,
          Machine: machine,
          Instrument: instrument,
          SourceItem: planBook,
          DriverId: driver?.Id,
          MachineId: machine?.Id,
          InstrumentId: instrument?.Id
        };
        return item;
      });
  }

  public getGridColumns(language: ILanguage, setting: ISettings): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: `${nameOf<IPpEntryPlanBookingGridItem>('Status')}.${nameOf<IGridStatus>('state')}`,
        class: `${nameOf<IPpEntryPlanBookingGridItem>('Status')}.${nameOf<IGridStatus>('class')}`,
        description: `${nameOf<IPpEntryPlanBookingGridItem>('Status')}.${nameOf<IGridStatus>('description')}`,
        title: '',
        filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: `${nameOf<IPpEntryPlanBookingGridItem>('FieldNumber')}`,
        title: 'Docu_Ini__Number',
        hide: true,
        width: 40,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: `${nameOf<IPpEntryPlanBookingGridItem>('FieldSubnumber')}`,
        title: 'Docu_Ini_Subnumber',
        width: 40,
        hide: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: `${nameOf<IPpEntryPlanBookingGridItem>('FieldNumberWithSubnumber')}`,
        title: 'Docu_Ini__Number',
        hide: new ApDynGridColumnHideConfig({
          mapHide: false,
          selfHide: this.mapViewStore.Listen(s => s.mode).pipe(map((m) => m !== MapViewMode.NORMAL)),
        }),
        width: 50,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: `${nameOf<IPpEntryPlanBookingGridItem>('FieldName')}`,
        title: 'Docu_Ini__Name',
        width: 110,
        cssClass: 'left',
        sortIndex: 0,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: `${nameOf<IPpEntryPlanBookingGridItem>('AreaHa')}`,
        title: 'Docu_Ini__Area',
        width: 50,
        headerFilterable: true,
        tooltip: (dataItem: IPpEntryPlanBookingGridItem) => this.roundNumericService.stringify(dataItem?.AreaHa, 4),
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [setting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        field: `${nameOf<IPpEntryPlanBookingGridItem>('Crop')}`,
        title: 'Global__Crop',
        width: 80,
        hide: true,
        cropColoring: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: `${nameOf<IPpEntryPlanBookingGridItem>('EcValue')}`,
        title: 'Global__EC',
        width: 50,
        hide: true,
        cssClass: 'right',
        filterType: FilterType.NUMBER
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Product',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: `${nameOf<IPpEntryPlanBookingGridItem>('ProductAverages')}`,
            title: 'Global__Avg_Symbol',
            tooltip: (dataItem: IPpEntryPlanBookingGridItem) => dataItem?.ProductAveragesTooltip,
            width: 150,
            multiline: true,
            filterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: `${nameOf<IPpEntryPlanBookingGridItem>('ProductSum')}`,
            title: 'Global__Sum',
            tooltip: (dataItem: IPpEntryPlanBookingGridItem) => dataItem?.ProductSumTooltip,
            width: 80,
            multiline: true,
            filterable: true
          })
        ]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Water',
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: `${nameOf<IPpEntryPlanBookingGridItem>('WaterRate')}`,
            title: 'Global__Avg_Symbol',
            hide: true,
            width: 60,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            template: PropertyTemplate.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0, {ToReplace: [PpNoDataValue, undefined], ReplaceWith: ''}]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: `${nameOf<IPpEntryPlanBookingGridItem>('WaterSum')}`,
            title: 'Global__Sum',
            tooltip: (dataItem: IPpEntryPlanBookingGridItem) => dataItem?.WaterSumTooltip,
            hide: true,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            template: PropertyTemplate.NUMBER,
            width: 60,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0, {ToReplace: [PpNoDataValue, undefined], ReplaceWith: ''}]
            }]
          })
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        field: `${nameOf<IPpEntryPlanBookingGridItem>('ApplicationDate')}`,
        title: 'Global_Application_Date',
        hide: true,
        width: 80,
        filterable: true,
        format: language.DateFormat,
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IPpEntryPlanBookingGridItem>('DurationInMilliseconds'),
        title: `${this.translationService.translate('TotalTime')} [${this.translationService.translate('Base__UnitH')}]`,
        hide: true,
        width: 70,
        cssClass: 'right',
        headerFilterable: true,
        tooltip: (dataItem: IPpEntryPlanBookingGridItem) => this.tooltipContentService.getCapacityPerformanceTooltip(dataItem.HaPerHour),
        pipes: [{
          pipe: this.timeFormatPipe,
          args: [{
            formatType: FormatType.Decimals,
            suffixValue: 'Base__UnitH',
            decimalsFormatOptions: {
              decimalsCount: 1,
              decimalsSeparator: language.DecimalSeperator
            }
          }]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Settings__Lbl_Menu_Resources',
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: `${nameOf<IPpEntryPlanBookingGridItem>('Machine')}.${nameOf<IMachine>('NameWithSign')}`,
            title: 'Base__Engine',
            hide: true,
            width: 100
          }),
          new ApDynGridPropertyColumnConfig({
            field: `${nameOf<IPpEntryPlanBookingGridItem>('Instrument')}.${nameOf<IInstrument>('Name')}`,
            title: 'AgriConnect_Ini__Tool',
            hide: true,
            width: 100
          }),
          new ApDynGridPropertyColumnConfig({
            field: `${nameOf<IPpEntryPlanBookingGridItem>('Driver')}.${nameOf<IDriver>('FirstNameFullName')}`,
            title: 'Docu_Ini__Motorist',
            hide: true,
            width: 100
          })
        ]
      }),
      new ApDynGridEditColumnConfig({})
    ];
  }

  public updateGridItems(gridItem: IPpEntryPlanBookingGridItem,
                         editData: IPpEntryPlanBookingEditData, nullableCheckEnabled: boolean): void {
    if (!gridItem || !editData) {
      return;
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && !!editData.ApplicationDate)) {
      gridItem.ApplicationDate = this.dateService.getDateNoon(editData.ApplicationDate);
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && !!editData.HaPerHour)) {
      const duration = this.timeAreaService.getHoursPerHaFromTotalMilliseconds(gridItem.AreaHa,
        editData.HaPerHour);
      gridItem.HaPerHour = editData.HaPerHour;
      gridItem.DurationInMilliseconds = duration;
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && !!editData.MachineId)) {
      gridItem.MachineId = editData.MachineId;
      gridItem.Machine = this.machineStore.getMachineById(editData.MachineId);
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && !!editData.InstrumentId)) {
      gridItem.InstrumentId = editData.InstrumentId;
      gridItem.Instrument = this.instrumentStore.getInstrumentById(editData.InstrumentId);
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && !!editData.DriverId)) {
      gridItem.DriverId = editData.DriverId;
      gridItem.Driver = this.driverStore.getDriverById(editData.DriverId);
    }
  }

  private getStatus(planBook: IPlantProtectionPlanBook, isBooked: boolean): IGridStatus {
    const status = this.nutrientService.getFertilizerStatus(planBook.ApplicationMode, isBooked);
    status.description = this.nutrientService.getFertilizerStatusDescription(planBook.ApplicationMode, isBooked);
    return status;
  }
}
